import { Injectable, inject } from '@angular/core';
import { catchError, Observable, take, throwError, of, mergeMap, from, toArray } from 'rxjs';
import { ConfigService } from 'src/services/json-config.service';
import {EAppearanceMode, IClientAccountPersonalInformationOut, IClientCollaboratorOut, ILoggedUser} from 'src/models/user.model';
import { AuthDataService } from 'src/data-store/auth-data.service';
import { UserDataService } from 'src/data-store/user-data.service';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { EnvironmentKeys } from 'src/environments/environment-keys';
import {
  IModelPortfolioOut,
  IPortfolioDetail,
  IPortfolioFeeConfigs,
  IPortfolioOverviewPage,
  IPortfolioInstrumentRatios,
  IPortfolioDetailChartParams,
  IModelPortfolioByRiskScore,
  IEntryFeeCalculationIn,
  IEntryFeeCalculationWithAllRatiosOut,
  IModelPortfolioChartInput,
  INewPortfolioInput,
  IInstrumentDetailFullOut,
  IBaseInstrument,
  IStrategyLimitOut,
  IModelStrategyChartInput,
  INewStrategyInput,
  IDraftsResponse,
  IApiThematicStrategy,
  IModelProductWithInstrumentsOut,
  IBackcastingChartInput,
  IClientRequestInput,
  IPortfolioDepositOut,
  IUpdatePortfolioV2Input,
  IUpdateStrategyInput,
  IDraftStatusResponse,
  IAcceptDraftResponse,
  IUpdateEfektikaStrategyInput,
  IClientAccountAdditionOut,
  EClientAccountAdditionNewIn,
  IEmailVerificationOut,
  IInvestmentInstrumentRemoval,
  IRegularWithdrawal,
  IRegularWithdrawalDraftsResponse,
  IRegularWithdrawalDraft,
  ERegularWithdrawalFrequency,
  IRegularWithdrawalLimits,
  EWithdrawalPortfolioType,
  INewRegularWithdrawalInput,
  IWithdrawableAmount,
} from 'src/models/products.model';
import { IHotlineAvailability, IImageUploadMaxSize, IPageRequest, IImageUploadProps, IPresignedUrl, ECurrencyCode } from 'src/models/common.model';
import dayjs from 'dayjs';
import { ICalendarOut, IDashboardChartInput, IDashboardSummary } from 'src/models/dashboard.model';
import { IFaq, IFaqByPage, IFaqCategory, IFaqCategoryDetail } from 'src/models/faq.model';
import {
  ExportTransactionsIn, GenerateDocumentLinkOut,
  IAllInvestmentsListItem,
  ITransactionsPage,
  ITransactionsQueryParams
} from 'src/models/transactions.model';
import {
  IBackcastingChartOut,
  IHistoryChartOut,
  IInstrumentChartInput,
  IInstrumentChartOut,
  IInvestmentModelChartOut} from 'src/models/charts.model';
import { IBlogArticleDetail, IBlogArticlesPage } from 'src/models/blog.model';
import { IBankAccount } from 'src/models/bank-accounts.model';
import {
  IDocumentsQueryParams,
  PagedResultDocumentOut
} from '../models/documents.model';
import { desktopWidthBreakpoint } from 'src/util/constants';
import { EApiDevicePlatforms, ENotificationKind, INotificationCount, INotificationResponse, IPushNotificationsRequestInput } from 'src/models/app.model';
import { DeviceService } from './device.service';
import { getAppHeaders } from 'src/util/functions';

@Injectable({ providedIn: 'root' })
export class RestApiService {
  private http = inject(HttpClient);
  private configService = inject(ConfigService);
  private authDataService = inject(AuthDataService);
  private userDataService = inject(UserDataService);
  private deviceService = inject(DeviceService);

  private getAppHeaders(accept: 'application/json' | 'image/jpeg' = 'application/json') {
    return getAppHeaders({
      accept,
      accessToken: this.authDataService.accessToken(),
      isNativeApp: this.deviceService.isNativePlatform,
    })
  }

  private get loggedUser(): ILoggedUser | null {
    return this.userDataService.loggedUser();
  }

  private handleError(error: HttpErrorResponse): Observable<never> {
    return throwError(() => error);
  }

  // GET /api/v1/calendar/date
  public getCalendar(from: dayjs.Dayjs, to: dayjs.Dayjs): Observable<ICalendarOut> {
    return this.http
      .get<ICalendarOut>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}v1/calendar/date`,
        {
          headers: this.getAppHeaders(),
          params: {
            fromDate: from.format('YYYY-MM-DD'),
            toDate: to.format('YYYY-MM-DD'),
          }
        },
      )
      .pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // GET /api/v1/client/{clientAccountId}/portfolio
  public getPortfolioOverviews(pageRequest?: IPageRequest): Observable<IPortfolioOverviewPage> {
    return this.http
      .get<IPortfolioOverviewPage>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}v1/client/${this.loggedUser?.clientAccountId}/portfolio`,
        {
          headers: this.getAppHeaders(),
          params: this.toHttpParams(pageRequest)
        },
      )
      .pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // GET /api/v1/clientAccount/{clientAccountId}/draftPortfolio
  public getClientInvestmentDrafts(): Observable<IDraftsResponse> {
    return this.http
      .get<IDraftsResponse>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}v1/clientAccount/${this.loggedUser?.clientAccountId}/draftPortfolio`,
        {
          headers: this.getAppHeaders(),
        },
      )
      .pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // GET /api/v1/client/{id}/draftPortfolio/{draftPortfolioId}/collaborator-photo
  public getConsultImageForDraftInvestment(draftPortfolioId: number): Observable<Blob> {
    return this.http
    .get<Blob>(
      `${this.configService.getConfig(EnvironmentKeys.apiUrl)}v1/client/${this.loggedUser?.clientAccountId}/draftPortfolio/${draftPortfolioId}/collaborator-photo`,
      {
        responseType: 'blob' as 'json',
        headers: this.getAppHeaders('image/jpeg'),
      },
    ).pipe(
        take(1),
    );
  }

  // GET /api/v1/clientAccount/{clientAccountId}/draftPortfolio/{draftPortfolioId}/status
  public getDraftInvestmentStatus(draftInvestmentId: number): Observable<IDraftStatusResponse> {
    return this.http
    .get<IDraftStatusResponse>(
      `${this.configService.getConfig(EnvironmentKeys.apiUrl)}v1/clientAccount/${this.loggedUser?.clientAccountId}/draftPortfolio/${draftInvestmentId}/status`,
      {
        headers: this.getAppHeaders(),
      },
    ).pipe(
        take(1)
    );
  }

  // POST /api/v1/clientAccount/{clientAccountId}/draftPortfolio/{draftPortfolioId}/accept
  public acceptDraftInvestment(draftPortfolioId: number): Observable<IAcceptDraftResponse> {
    return this.http
      .post<IAcceptDraftResponse>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}v1/clientAccount/${this.loggedUser?.clientAccountId}/draftPortfolio/${draftPortfolioId}/accept`,
        {},
        {
          headers: this.getAppHeaders(),
        },
      ).pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // POST /api/v1/clientAccount/{clientAccountId}/draftPortfolio/{draftPortfolioId}/refuse
  public refuseDraftInvestment(draftPortfolioId: number): Observable<null> {
    return this.http
      .post<null>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}v1/clientAccount/${this.loggedUser?.clientAccountId}/draftPortfolio/${draftPortfolioId}/refuse`,
        {},
        {
          headers: this.getAppHeaders(),
        },
      ).pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // GET /api/v1/client/{clientAccountId}/portfolio/{portfolioId}
  public getSelectedPortfolio(portfolioId: number): Observable<IPortfolioDetail> {
    return this.http
      .get<IPortfolioDetail>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}v1/client/${this.loggedUser?.clientAccountId}/portfolio/${portfolioId}`,
        {
          headers: this.getAppHeaders(),
        },
      )
      .pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // GET /api/v2/client/{clientAccountId}/portfolio/{portfolioId}/fee
  public getPortfolioFeeConfigs(portfolioId: number): Observable<IPortfolioFeeConfigs> {
    return this.http
      .get<IPortfolioFeeConfigs>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}v2/client/${this.loggedUser?.clientAccountId}/portfolio/${portfolioId}/fee`,
        {
          headers: this.getAppHeaders(),
        },
      )
      .pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // GET /api/v2/client/{clientAccountId}/transaction
  public getTransactions(queryParams?: ITransactionsQueryParams): Observable<ITransactionsPage> {
    const params: Record<string, unknown> = {};

    if (queryParams?.portfolioIds !== undefined) {
      params['portfolioIds'] = queryParams.portfolioIds;
    }
    if (queryParams?.transactionTimestampFrom) {
      params['transactionTimestampFrom'] = queryParams.transactionTimestampFrom;
    }
    if (queryParams?.transactionTimestampTo) {
      params['transactionTimestampTo'] = queryParams.transactionTimestampTo;
    }
    if (queryParams?.type && queryParams.type.length > 0) {
      params['type'] = queryParams.type;
    }
    if (queryParams?.page !== undefined) {
      params['page'] = queryParams.page - 1; // first page starts at 0
    }
    if (queryParams?.size !== undefined) {
      params['size'] = queryParams.size;
    }
    if (queryParams?.query) {
      params['query'] = queryParams.query;
    }

    return this.http
      .get<ITransactionsPage>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}v2/client/${this.loggedUser?.clientAccountId}/transaction`,
        {
          headers: this.getAppHeaders(),
          params: this.toHttpParams(params),
        },
      )
      .pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // GET /api/v1/client/{clientAccountId}/portfolio/all
  public getAllInvestments(): Observable<IAllInvestmentsListItem[]> {
    return this.http
    .get<IAllInvestmentsListItem[]>(
      this.configService.getConfig(EnvironmentKeys.apiUrl) + 'v1/client/' + this.loggedUser?.clientAccountId + '/portfolio/all',
      {
        headers: this.getAppHeaders(),
      },
    ).pipe(
        take(1),
    );
  }

  private toHttpParams(paramsObj?: Record<string, unknown>): HttpParams {
    let params = new HttpParams();
    if(paramsObj) {
      Object.entries(paramsObj).forEach(([key, value]) => {
        if (value !== null) {
          if (Array.isArray(value)) {
            value.forEach(v => params = params.append(key, String(v)));
          } else {
            params = params.set(key, String(value));
          }
        }
      });
    }
    return params;
  }

  // GET /api/v1/faq/page/{pageCode}
  public getFaqsByPageCode(pageCode: string): Observable<IFaqByPage> {
    return this.http
      .get<IFaqByPage>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}v1/faq/page/${pageCode}`,
        {
          headers: this.getAppHeaders(),
        },
      )
      .pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // GET /api/v1/faq/categories
  public getFaqCategories(): Observable<IFaqCategory[]> {
    return this.http
      .get<IFaqCategory[]>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}v1/faq/categories`,
        {
          headers: this.getAppHeaders(),
        },
      )
      .pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // GET /api/v1/faq/category/{categoryId}
  public getFaqCategory(categoryId: number): Observable<IFaqCategoryDetail> {
    return this.http
      .get<IFaqCategoryDetail>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}v1/faq/category/${categoryId}`,
        {
          headers: this.getAppHeaders(),
        },
      )
      .pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // GET /api/v1/faq/search?query={query}
  public searchFaqs(query: string): Observable<IFaq[]> {
    return this.http
      .get<IFaq[]>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}v1/faq/search`,
        {
          headers: this.getAppHeaders(),
          params: {
            query: query,
          }
        },
      )
      .pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // GET /api/v3/client/{clientAccountId}/summary
  public getDashboardSummary(): Observable<IDashboardSummary> {
    return this.http
      .get<IDashboardSummary>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}v3/client/${this.loggedUser?.clientAccountId}/summary`,
        {
          headers: this.getAppHeaders(),
        },
      )
      .pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // GET /api/v1/client/{clientAccountId}/summary/graph
  public  getSummaryChart(params: IDashboardChartInput): Observable<IHistoryChartOut> {
    const defaultTicks = window.innerWidth >= desktopWidthBreakpoint ? 100 : 25;

    return this.http
      .get<IHistoryChartOut>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}v1/client/${this.loggedUser?.clientAccountId}/summary/graph`,
        {
          headers: this.getAppHeaders(),
          params: {
            currencyCode: params.currencyCode ?? this.loggedUser?.currencyCode ?? ECurrencyCode.CZK,
            fromDate: params.fromDate?.toISOString().slice(0, 10) ?? '',
            toDate: params.toDate?.toISOString().slice(0, 10) ?? '',
            size: params.size ?? defaultTicks,
            priceMeasure: params.priceMeasure ?? 'VALUE',
          },
        },
      )
      .pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // GET /api/v1/hotline
  public getHotlineAvailability(): Observable<IHotlineAvailability> {
    return this.http
      .get<IHotlineAvailability>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}v1/hotline`,
        {
          headers: this.getAppHeaders(),
        },
      )
      .pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // GET /api/v1/client/{clientId}/portfolio/{portfolioId}/model-portfolio
  public getModelPortfolio(portfolioId: number): Observable<IModelPortfolioOut> {
    return this.http
      .get<IModelPortfolioOut>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}v1/client/${this.loggedUser?.clientAccountId}/portfolio/${portfolioId}/model-portfolio`,
        {
          headers: this.getAppHeaders(),
        },
      ).pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // GET /api/v1/client/{clientId}/modelPortfolio/{riskScore}
  public getModelPortfolioByRiskScore(riskScore: number): Observable<IModelPortfolioByRiskScore> {
    return this.http
    .get<IModelPortfolioByRiskScore>(
      `${this.configService.getConfig(EnvironmentKeys.apiUrl)}v1/client/${this.loggedUser?.clientAccountId}/modelPortfolio/${riskScore}`,
      {
        headers: this.getAppHeaders(),
      },
    )
    .pipe(
      take(1),
      catchError(this.handleError.bind(this)),
    );
  }

  // GET /api/v2/client/{clientAccountId}/portfolio/{portfolioId}/portfolio-instrument
  public getPortfolioInstrumentRatios(portfolioId: number): Observable<IPortfolioInstrumentRatios> {
    return this.http
      .get<IPortfolioInstrumentRatios>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}v2/client/${this.loggedUser?.clientAccountId}/portfolio/${portfolioId}/portfolio-instrument`,
        {
          headers: this.getAppHeaders(),
        },
      ).pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // GET /api/v1/client/{clientAccountId}/photo
  public getClientProfilePhoto(): Observable<IPresignedUrl> {
    return this.http
      .get<IPresignedUrl>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}v1/client/${this.loggedUser?.clientAccountId}/photo`,
        {
          headers: this.getAppHeaders(),
        },
      )
      .pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // PUT /api/v1/client/{clientAccountId}/photo
  public requestUpdateClientProfilePhoto(imageProps: IImageUploadProps): Observable<IPresignedUrl> {
    return this.http
      .put<IPresignedUrl>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}v1/client/${this.loggedUser?.clientAccountId}/photo`,
        imageProps,
        {
          headers: this.getAppHeaders(),
        },
      )
      .pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // PUT /api/v1/client/{clientAccountId}/portfolio/{portfolioId}/image
  public requestUpdatePortfolioPhoto(portfolioId: number, imageProps: IImageUploadProps): Observable<IPresignedUrl> {
    return this.http
      .put<IPresignedUrl>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}v1/client/${this.loggedUser?.clientAccountId}/portfolio/${portfolioId}/image`,
        imageProps,
        {
          headers: this.getAppHeaders(),
        },
      )
      .pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // POST /api/v1/client/{clientAccountId}/photo
  public confirmClientProfilePhotoUpdated(): Observable<void> {
    return this.http
      .post<void>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}v1/client/${this.loggedUser?.clientAccountId}/photo`,
        {},
        {
          headers: this.getAppHeaders(),
        },
      )
      .pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // POST /api/v1/client/{clientAccountId}/portfolio/{portfolioId}/image
  public confirmPortfolioPhotoUpdated(portfolioId: number): Observable<void> {
    return this.http
      .post<void>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}v1/client/${this.loggedUser?.clientAccountId}/portfolio/${portfolioId}/image`,
        {},
        {
          headers: this.getAppHeaders(),
        },
      )
      .pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // DELETE /api/v1/client/{clientAccountId}/photo
  public deleteClientProfilePhoto(): Observable<void> {
    return this.http
      .delete<void>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}v1/client/${this.loggedUser?.clientAccountId}/photo`,
        {
          headers: this.getAppHeaders(),
        },
      )
      .pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // DELETE /api/v1/client/{clientAccountId}/portfolio/{portfolioId}/image
  public deleteInvestmentPhoto(portfolioId: number): Observable<void> {
    return this.http
      .delete<void>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}v1/client/${this.loggedUser?.clientAccountId}/portfolio/${portfolioId}/image`,
        {
          headers: this.getAppHeaders(),
        },
      )
      .pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // GET /api/v1/image/maxsize
  public getImageUploadMaxSize(): Observable<IImageUploadMaxSize> {
    return this.http
      .get<IImageUploadMaxSize>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}v1/image/maxsize`,
        {
          headers: this.getAppHeaders(),
        },
      )
      .pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  public minioRequest(url: string, method: 'GET' | 'PUT', responseType: 'blob' | 'json', body?: Blob): Observable<Blob | void | null> {
    return this.http
      .request(method, url, { responseType, body })
      .pipe(
        take(1),
        catchError(() => of(null)),
      );
  }

  // GET /api/v1/client/{id}/collaborator/info
  public getUserConsult(): Observable<IClientCollaboratorOut> {
    return this.http
    .get<IClientCollaboratorOut>(
      `${this.configService.getConfig(EnvironmentKeys.apiUrl)}v1/client/${this.loggedUser?.clientAccountId}/collaborator/info`,
      {
        headers: this.getAppHeaders(),
      },
    ).pipe(
        take(1),
        catchError(this.handleError.bind(this)),
    );
  }

  // GET /api/v1/client/{id}/collaborator/photo
  public getConsultantPhoto(): Observable<Blob> {
    return this.http
    .get<Blob>(
      `${this.configService.getConfig(EnvironmentKeys.apiUrl)}v1/client/${this.loggedUser?.clientAccountId}/collaborator/photo`,
      {
        responseType: 'blob' as 'json',
        headers: this.getAppHeaders('image/jpeg'),
      },
    ).pipe(
        take(1),
    );
  }

  // GET /api/v1/client/{clientAccountId}/portfolio/{portfolioId}/graph
  public getInvestmentChart(params: IPortfolioDetailChartParams, portfolioId: number): Observable<IHistoryChartOut> {
    // todo ask: will we be doing this ? how maby ticks?
    const desktopWidthBreakpoint = 768;
    const defaultTicks = window.innerWidth >= desktopWidthBreakpoint ? 100 : 25;

    return this.http
      .get<IHistoryChartOut>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}v1/client/${this.loggedUser?.clientAccountId}/portfolio/${portfolioId}/graph`,
        {
          headers: this.getAppHeaders(),
          params: {
            currencyCode: params.currencyCode ?? this.loggedUser?.currencyCode ?? ECurrencyCode.CZK,
            fromDate: params.fromDate?.format('YYYY-MM-DD') ?? '',
            toDate: params.toDate?.format('YYYY-MM-DD') ?? '',
            size: params.size ?? defaultTicks,
            priceMeasure: params.priceMeasure ?? 'VALUE',
          },
        },
      )
      .pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // GET /api/v1/model-strategies/rotation
  public getThematicStrategiesRotation(limit: number): Observable<IApiThematicStrategy[]> {
    return this.http
      .get<IApiThematicStrategy[]>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}v1/client/${this.loggedUser?.clientAccountId}/model-strategies/rotation`,
        {
          headers: this.getAppHeaders(),
          params: {
            limit
          },
        }
    );
  }

  // GET /api/v1/blog
  public getBlogArticlesPage(pageRequest: IPageRequest, search: string | null): Observable<IBlogArticlesPage> {
    const params: Record<string, unknown> = { ...pageRequest }
    if (search) {
      params['search'] = search
    }

    return this.http
      .get<IBlogArticlesPage>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}v1/blog`,
        {
          headers: this.getAppHeaders(),
          params: this.toHttpParams(params),
        },
      )
      .pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // GET /api/v1/blog/{articleId}
  public getBlogArticleDetail(articleId: number): Observable<IBlogArticleDetail> {
    return this.http
      .get<IBlogArticleDetail>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}v1/blog/${articleId}`,
        {
          headers: this.getAppHeaders(),
        },
      )
      .pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // POST /api/v1/fee-calculation
  public calculateEntryFeeForNewInvestment(calcInput: IEntryFeeCalculationIn): Observable<IEntryFeeCalculationWithAllRatiosOut> {
    return this.http
    .post<IEntryFeeCalculationWithAllRatiosOut>(
      `${this.configService.getConfig(EnvironmentKeys.apiUrl)}v1/fee-calculation`,
      calcInput,
      {
        headers: this.getAppHeaders(),
      },
    ).pipe(
      take(1),
      catchError(this.handleError.bind(this)),
    );
  }

  // POST /api/v1/client/{clientAccountId}/portfolio/{portfolioId}/entry-fee
  public calculateEntryFeeForExistingInvestment(calcInput: IEntryFeeCalculationIn, portfolioId: number): Observable<IEntryFeeCalculationWithAllRatiosOut> {
    return this.http
    .post<IEntryFeeCalculationWithAllRatiosOut>(
      `${this.configService.getConfig(EnvironmentKeys.apiUrl)}v1/client/${this.loggedUser?.clientAccountId}/portfolio/${portfolioId}/entry-fee`,
      calcInput,
      {
        headers: this.getAppHeaders(),
      },
    ).pipe(
      take(1),
      catchError(this.handleError.bind(this)),
    );
  }

  // POST /api/v1/client/{clientId}/modelPortfolio/{riskScore}/chart
  public getModelPortfolioChart(params: IModelPortfolioChartInput): Observable<IInvestmentModelChartOut> {
    return this.http
      .post<IInvestmentModelChartOut>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}v1/client/${this.loggedUser?.clientAccountId}/modelPortfolio/${params.riskProfile}/chart`,
        {
          weeksBetweenInvestments: params.depositRegularity,
          yearsToInvest: params.timeOfInvestment,
          initialDeposit: params.oneTimeDeposit,
          regularDeposit: params.regularDeposit
        },
        {
          headers: this.getAppHeaders(),
        },
      ).pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // POST /api/v3/strategy/backcasting-chart
  public getBackcastingChart(options: IBackcastingChartInput): Observable<IBackcastingChartOut> {
    return this.http
      .post<IBackcastingChartOut>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}v3/strategy/backcasting-chart`,
        options,
        {
          headers: this.getAppHeaders(),
        },
      ).pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // POST /api/v1/strategy/model-chart
  public getStrategyModelChart(options: IModelStrategyChartInput): Observable<IInvestmentModelChartOut> {
    return this.http
      .post<IInvestmentModelChartOut>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}v1/strategy/model-chart`,
        options,
        {
          headers: this.getAppHeaders(),
        },
      ).pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // POST /api/v1/clientAccount/{clientAccountId}/frameworkContract/{frameworkContractId}/portfolio
  public postPortfolio(portfolioData: INewPortfolioInput): Observable<{ portfolioId: number }> {
    return this.http
      .post<{ portfolioId: number }>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}v1/clientAccount/${this.loggedUser?.clientAccountId}/frameworkContract/${portfolioData.frameworkContractId}/portfolio`,
        portfolioData,
        {
          headers: this.getAppHeaders(),
        }
      )
      .pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // POST /api/v1/client/{id}/collaborator/meeting
  public requestConsultMeeting(): Observable<void> {
    return this.http
    .post<void>(
      `${this.configService.getConfig(EnvironmentKeys.apiUrl)}v1/client/${this.loggedUser?.clientAccountId}/collaborator/meeting`,
      {},
      {
        headers: this.getAppHeaders(),
      },
    ).pipe(
      take(1),
      catchError(this.handleError.bind(this)),
    );
  }

  // POST /api/v1/client/{clientAccountId}/can-read-english
  public postCanReadEnglish(): Observable<void> {
    return this.http
      .post<void>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}v1/client/${this.loggedUser?.clientAccountId}/can-read-english`,
        {},
        {
          headers: this.getAppHeaders(),
        },
      ).pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // POST /api/v1/clientAccount/{clientAccountId}/frameworkContract/{frameworkContractId}/strategy
  public postStrategy(strategyData: INewStrategyInput): Observable<{ portfolioId: number }> {
    return this.http
      .post<{ portfolioId: number }>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}v1/clientAccount/${this.loggedUser?.clientAccountId}/frameworkContract/${strategyData.frameworkContractId}/strategy`,
        {
          ...strategyData
        },
        {
          headers: this.getAppHeaders(),
        },
      )
      .pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // GET /api/v1/blog/preview (unauthorized)
  public getBlogArticlePreview(token: string): Observable<IBlogArticleDetail> {
    return this.http
      .get<IBlogArticleDetail>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}v1/blog/preview`,
        {
          params: { token },
        },
      )
      .pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // GET /api/v1/instrument/detail
  public getInstruments(canReadEnglishOverride?: boolean): Observable<IInstrumentDetailFullOut[]> {
    return this.http
      .get<IInstrumentDetailFullOut[]>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}v1/instrument/detail`,
        {
          headers: this.getAppHeaders(),
          params: {
            withIsInEnglish: canReadEnglishOverride ?? (this.loggedUser?.canReadEnglish ?? false),
          }
        },
      ).pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // GET /api/v1/instrument/public-detail
  public getInstrumentsPublic(): Observable<IInstrumentDetailFullOut[]> {
    return this.http
      .get<IInstrumentDetailFullOut[]>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}v1/instrument/public-detail`,
        {
          headers: this.getAppHeaders(),
        },
      ).pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // POST /api/v1/client/{clientAccountId}/portfolio/{portfolioId}/temporary-document/employer-payment
  public employerPaymentPdf(portfolioId: number): Observable<{ link: string }> {
    return this.http
    .post<{ link: string }>(
      `${this.configService.getConfig(EnvironmentKeys.apiUrl)}v1/client/${this.loggedUser?.clientAccountId}/portfolio/${portfolioId}/temporary-document/employer-payment`,
      {},
      {
        headers: this.getAppHeaders(),
      },
    ).pipe(
      take(1),
      catchError(this.handleError.bind(this)),
    );
  }

  // DELETE /api/v1/client/{clientAccountId}/portfolio/{portfolioId}/close
  public deleteInvestment(portfolioId: number): Observable<void> {
    return this.http
    .delete<void>(
      `${this.configService.getConfig(EnvironmentKeys.apiUrl)}v1/client/${this.loggedUser?.clientAccountId}/portfolio/${portfolioId}/close`,
      {
        headers: this.getAppHeaders(),
      },
    ).pipe(
      take(1),
      catchError(this.handleError.bind(this)),
    );
  }

  // GET /api/v1/instrument/cash
  public getCashInstruments(): Observable<IBaseInstrument[]> {
    return this.http
      .get<IBaseInstrument[]>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}v1/instrument/cash`,
        {
          headers: this.getAppHeaders(),
        },
      )
      .pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // GET /api/v1/strategy/limit
  public getStrategyLimits(): Observable<IStrategyLimitOut> {
    return this.http
      .get<IStrategyLimitOut>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}v1/strategy/limit`,
        {
          headers: this.getAppHeaders(),
        },
      ).pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // GET /api/v1/client/{clientAccountId}/modelStrategy
  public getThematicStrategies(): Observable<IApiThematicStrategy[]> {
    return this.http
      .get<IApiThematicStrategy[]>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}v1/client/${this.loggedUser?.clientAccountId}/modelStrategy`,
        {
          headers: this.getAppHeaders(),
        },
      ).pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // GET /api/v1/client/{clientAccountId}/personal-information
  public getPersonalInformation(): Observable<IClientAccountPersonalInformationOut> {
    return this.http
      .get<IClientAccountPersonalInformationOut>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}v1/client/${this.loggedUser?.clientAccountId}/personal-information`,
        {
          headers: this.getAppHeaders(),
        },
      ).pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      )
  }

  // GET api/v1/modelStrategy/{id}
  public getModelStrategy(modelStrategyId: number): Observable<IModelProductWithInstrumentsOut> {
    return this.http
      .get<IModelProductWithInstrumentsOut>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}v1/modelStrategy/${modelStrategyId}`,
        {
          headers: this.getAppHeaders(),
        },
      ).pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // GET /api/v1/client/{clientAccountId}/portfolio/{portfolioId}/deposit
  public getMoneyDeposit(params: IClientRequestInput): Observable<IPortfolioDepositOut> {
    return this.http
      .get<IPortfolioDepositOut>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}v1/client/${this.loggedUser?.clientAccountId}/portfolio/${params.portfolioId}/deposit`,
        {
          headers: this.getAppHeaders(),
        },
      ).pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // GET /api/v1/client/{clientAccountId}/portfolio/{portfolioId}/bank-account
  public getBankAccounts(portfolioId: number): Observable<IBankAccount[]> {
    return this.http
      .get<IBankAccount[]>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}v1/client/${this.loggedUser?.clientAccountId}/portfolio/${portfolioId}/bank-account`,
        {
          headers: this.getAppHeaders(),
        },
      ).pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // PUT /api/v2/clientAccount/{clientAccountId}/portfolio/{portfolioId}
  public updatePortfolio(input: IUpdatePortfolioV2Input, portfolioId: number): Observable<void> {
    return this.http
    .put<void>(
      `${this.configService.getConfig(EnvironmentKeys.apiUrl)}v2/clientAccount/${this.loggedUser?.clientAccountId}/portfolio/${portfolioId}`,
      input,
      {
        headers: this.getAppHeaders(),
      },
    ).pipe(
      take(1),
      catchError(this.handleError.bind(this)),
    );
  }

  // PUT api/v1/clientAccount/{clientAccountId}/strategy/{strategyId}
  public updateStrategy(input: IUpdateStrategyInput, strategyId: number): Observable<void> {
    return this.http
    .put<void>(
      `${this.configService.getConfig(EnvironmentKeys.apiUrl)}v1/clientAccount/${this.loggedUser?.clientAccountId}/strategy/${strategyId}`,
      input,
      {
        headers: this.getAppHeaders(),
      },
    ).pipe(
      take(1),
      catchError(this.handleError.bind(this)),
    );
  }

  // GET api/v1/client/{clientAccountId}/document
  public getDocuments(queryParams?: IDocumentsQueryParams): Observable<PagedResultDocumentOut> {
    const params: Record<string, unknown> = {
      ...(queryParams?.issuedFrom && { issuedFrom: queryParams.issuedFrom.format('YYYY-MM-DD') }),
      ...(queryParams?.issuedTo && { issuedTo: queryParams.issuedTo.format('YYYY-MM-DD') }),
      ...(queryParams?.categories && queryParams.categories.length > 0 && { category: queryParams.categories }),
      ...(queryParams?.name && { name: queryParams.name }),
      ...(queryParams?.page !== undefined && { page: queryParams.page }),
      ...(queryParams?.size !== undefined && { size: queryParams.size }),
    };

    return this.http
      .get<PagedResultDocumentOut>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}v1/client/${this.loggedUser?.clientAccountId}/document`,
        {
          headers: this.getAppHeaders(),
          params: this.toHttpParams(params),
        }
      ).pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      )
  }

  // POST /api/v2/client/{clientAccountId}/document/{documentId}/link
  public generateDocumentLink(documentId: number): Observable<GenerateDocumentLinkOut> {
    return this.http
      .post<GenerateDocumentLinkOut>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}v2/client/${this.loggedUser?.clientAccountId}/document/${documentId}/link`,
        {
          resolveAll: true,
        },
        {
          headers: this.getAppHeaders(),
        },
      ).pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // POST /api/v1/client/{clientAccountId}/portfolio/{portfolioId}/withdrawall
  public withdrawAll(portfolioId: number, clientBankAccountId: number): Observable<void> {
    return this.http
      .post<void>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}v1/client/${this.loggedUser?.clientAccountId}/portfolio/${portfolioId}/withdrawall`,
        {
          clientBankAccountId,
        },
        {
          headers: this.getAppHeaders(),
        },
      ).pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // POST /api/v1/client/{clientAccountId}/portfolio/{portfolioId}/withdraw
  public withdrawInvestment(portfolioId: number, clientBankAccountId: number, amount: number): Observable<void> {
    return this.http
      .post<void>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}v1/client/${this.loggedUser?.clientAccountId}/portfolio/${portfolioId}/withdraw`,
        {
          clientBankAccountId,
          amount,
        },
        {
          headers: this.getAppHeaders(),
        },
      ).pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // POST /api/v1/temporary-document/transaction/export
  public generateTransactionsExport(params: ExportTransactionsIn): Observable<GenerateDocumentLinkOut> {
    return this.http
      .post<GenerateDocumentLinkOut>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}v1/temporary-document/transaction/export`,
        {},
        {
          headers: this.getAppHeaders(),
          params: { ...params },
        },
      ).pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // GET /api/v1/client/{clientAccountId}/addition
  public getClientAdditions(): Observable<IClientAccountAdditionOut[]> {
    return this.http
    .get<IClientAccountAdditionOut[]>(
      this.configService.getConfig(EnvironmentKeys.apiUrl) + 'v1/client/' + this.loggedUser?.clientAccountId + '/addition',
      {
        headers: this.getAppHeaders(),
      },
    ).pipe(
        take(1)
    );
  }

  // POST /api/v1/client/{clientAccountId}/addition
  public createClientAddition(type: EClientAccountAdditionNewIn): Observable<void> {
    return this.http
      .post<void>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}v1/client/${this.loggedUser?.clientAccountId}/addition`,
        {
          type
        },
        {
          headers: this.getAppHeaders(),
        },
      ).pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // PUT api/v1/clientAccount/{clientAccountId}/efektika/{portfolioId}
  public updateEfektikaStrategy(strategyUpdate: IUpdateEfektikaStrategyInput, strategyId: number): Observable<void> {
    return this.http
    .put<void>(
      `${this.configService.getConfig(EnvironmentKeys.apiUrl)}v1/clientAccount/${this.loggedUser?.clientAccountId}/efektika/${strategyId}`,
      strategyUpdate,
      {
        headers: this.getAppHeaders(),
      },
    ).pipe(
      take(1),
      catchError(this.handleError.bind(this)),
    );
  }

  // POST /api/v1/client/{id}/disclaimer-shown
  public setDisclaimerShown(): Observable<void> {
    return this.http
    .post<void>(
      `${this.configService.getConfig(EnvironmentKeys.apiUrl)}v1/client/${this.loggedUser?.clientAccountId}/disclaimer-shown`,
      {},
      {
        headers: this.getAppHeaders(),
      }
    )
    .pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // GET /api/v1/client/{clientId}/web-notification/processed-sum
  public getNewNotificationsCount(): Observable<INotificationCount> {
    return this.http
      .get<INotificationCount>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}v1/client/${this.loggedUser?.clientAccountId}/web-notification/processed-sum`,
        {
          headers: this.getAppHeaders(),
        },
      ).pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // GET /api/v1/client/{clientId}/web-notification
  public getAllNotifications(type: ENotificationKind, page = 1, pageSize = 10): Observable<INotificationResponse> {
    return this.http
      .get<INotificationResponse>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}v1/client/${this.loggedUser?.clientAccountId}/web-notification`,
        {
          headers: this.getAppHeaders(),
          params: {
            page: page - 1,
            size: pageSize,
            notificationKind: type,
          }
        },
      )
      .pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // POST /api/v1/client/{clientId}/web-notification/{notificationWebId}/resolve
  public resolveNotification(id: number, resolveAll?: true, notificationKind?: ENotificationKind): Observable<void> {
    return this.http
      .post<void>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}v1/client/${this.loggedUser?.clientAccountId}/web-notification/${id}/resolve`,
        {},
        {
          headers: this.getAppHeaders(),
          params: {
            resolveAll: !!resolveAll,
            // notificationKind allowed only when resolveAll is true
            ...(!!resolveAll && notificationKind ? { notificationKind } : {}),
          },
        },
      ).pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // POST /api/v1/client-account-addition/{uuid}
  public signClientAddition(token: string): Observable<void> {
    return this.http
      .post<void>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}v1/client-account-addition/${token}`,
        {},
        {
          headers: this.getAppHeaders(),
        },
      ).pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // POST /api/v1/email-verification/confirm/{token}
  public signEmail(token: string): Observable<IEmailVerificationOut> {
    return this.http
    .post<IEmailVerificationOut>(
      this.configService.getConfig(EnvironmentKeys.apiUrl) + 'v1/email-verification/confirm/' + token,
      {},
      {
        headers: this.getAppHeaders(),
      },
    ).pipe(
      take(1),
      catchError(this.handleError.bind(this)),
    );
  }

  // POST /api/v1/approveDipWithdrawal/{token}
  public approveDipWithdrawal(token: string): Observable<void> {
    return this.http
    .post<void>(
      this.configService.getConfig(EnvironmentKeys.apiUrl) + 'v1/approveDipWithdrawal/' + token,
      {},
      {
        headers: this.getAppHeaders(),
      },
    ).pipe(
      take(1),
      catchError(this.handleError.bind(this)),
    );
  }

  // GET /api/v1/instrument/{instrumentId}/graph
  public instrumentChart(input: IInstrumentChartInput): Observable<IInstrumentChartOut> {
    return this.http
    .get<IInstrumentChartOut>(
      this.configService.getConfig(EnvironmentKeys.apiUrl) + 'v1/instrument/' + input.instrumentId + '/graph',
      {
        headers: this.loggedUser ? this.getAppHeaders() : undefined,
        params: {
          ...( input.fromDate && { fromDate: input.fromDate } ),
          ...( input.size && { size: input.size } ),
        }
      },
    ).pipe(
        take(1)
    );
  }

  // GET /api/v1/clientAccount/{clientAccountId}/strategy/{strategyId}/change-proposals
  public getInvestmentInstrumentRemovals(investmentId: number): Observable<IInvestmentInstrumentRemoval[]> {
    return this.http
      .get<IInvestmentInstrumentRemoval[]>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}v1/clientAccount/${this.loggedUser?.clientAccountId}/strategy/${investmentId}/change-proposals`,
        {
          headers: this.getAppHeaders(),
        },
      ).pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // POST /api/v1/clientAccount/{clientAccountId}/strategy/{strategyId}/change-proposals/{proposalId}/decline
  public refuseInstrumentsRemovalProposal(investmentId: number, proposalId: number): Observable<void> {
    return this.http
    .post<void>(
      `${this.configService.getConfig(EnvironmentKeys.apiUrl)}v1/clientAccount/${this.loggedUser?.clientAccountId}/strategy/${investmentId}/change-proposals/${proposalId}/decline`,
      {},
      {
        headers: this.getAppHeaders(),
      },
    ).pipe(
      take(1),
      catchError(this.handleError.bind(this)),
    );
  }

  // POST /api/v1/clientAccount/{clientAccountId}/strategy/{strategyId}/change-proposals/{proposalId}/accept
  public acceptInstrumentsRemovalProposal(investmentId: number, proposalId: number): Observable<void> {
    return this.http
    .post<void>(
      `${this.configService.getConfig(EnvironmentKeys.apiUrl)}v1/clientAccount/${this.loggedUser?.clientAccountId}/strategy/${investmentId}/change-proposals/${proposalId}/accept`,
      {},
      {
        headers: this.getAppHeaders(),
      },
    ).pipe(
      take(1),
      catchError(this.handleError.bind(this)),
    );
  }

  // POST /api/v1/device/create/{clientAccountId}
  public sendDeviceToken(options: IPushNotificationsRequestInput): Observable<void> {
    return this.http.post<void>(
      this.configService.getConfig(EnvironmentKeys.apiUrl) + 'v1/device/create/' + this.loggedUser?.clientAccountId,
      {
        token: options.deviceToken,
        type: options.platform,
      },
      {
        headers: this.getAppHeaders(),
      },
    )
    .pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // DELETE /api/v1/device/delete/{clientAccountId}
  public deleteDeviceToken(options: IPushNotificationsRequestInput): Observable<void> {
    return this.http
    .delete<void>(
      this.configService.getConfig(EnvironmentKeys.apiUrl) + 'v1/device/delete/' + this.loggedUser?.clientAccountId,
      {
        headers: this.getAppHeaders(),
        body: {
          token: options.deviceToken,
          type: options.platform,
        }
      },
    ).pipe(
      take(1),
      catchError(this.handleError.bind(this)),
    );
  }

  // GET /api/v1/mobile/validate-version
  public validatePhoneAppVersion(
    deviceType: EApiDevicePlatforms, version: string
  ): Observable<{ valid: boolean }> {
    return this.http
    .get<{ valid: boolean }>(
      this.configService.getConfig(EnvironmentKeys.apiUrl) + 'v1/mobile/validate-version',
      {
        headers: this.getAppHeaders(),
        params: {
          device: deviceType, version
        }
      },
    ).pipe(
        take(1)
    );
  }

  // GET /api/v1/client/{clientAccountId}/withdrawal-portfolio
  public getWithdrawalPortfoliosPage(page = 1, pageSize = 10): Observable<IPortfolioOverviewPage> {
    return this.http
      .get<IPortfolioOverviewPage>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}v1/client/${this.loggedUser?.clientAccountId}/withdrawal-portfolio`,
        {
          headers: this.getAppHeaders(),
          params: {
            page: page - 1,
            size: pageSize,
          }
        },
      )
      .pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // GET /api/v1/client/{clientAccountId}/regular-withdrawal
  public getRegularWithdrawals(): Observable<IRegularWithdrawal[]> {
    return this.http
      .get<IRegularWithdrawal[]>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}v1/client/${this.loggedUser?.clientAccountId}/regular-withdrawal`,
        {
          headers: this.getAppHeaders(),
        },
      )
      .pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // GET /api/v1/client/{clientAccountId}/regular-withdrawal
  public getRegularWithdrawalById(regularWithdrawalId: number): Observable<IRegularWithdrawal> {
    return this.http
      .get<IRegularWithdrawal>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}v1/client/${this.loggedUser?.clientAccountId}/regular-withdrawal/${regularWithdrawalId}`,
        {
          headers: this.getAppHeaders(),
        },
      )
      .pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // DELETE /api/v1/client/{clientAccountId}/regular-withdrawal/{regularWithdrawalId}
  public deleteRegularWithdrawal(regularWithdrawalId: number): Observable<void> {
    return this.http
      .delete<void>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}v1/client/${this.loggedUser?.clientAccountId}/regular-withdrawal/${regularWithdrawalId}`,
        {
          headers: this.getAppHeaders(),
        },
      )
      .pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // GET /api/v1/client/{clientAccountId}/regular-withdrawal-draft
  public getRegularWithdrawalDrafts(): Observable<IRegularWithdrawalDraft[]> {
    return this.http
    .get<IRegularWithdrawalDraftsResponse>(
      `${this.configService.getConfig(EnvironmentKeys.apiUrl)}v1/client/${this.loggedUser?.clientAccountId}/regular-withdrawal-draft`,
      {
        headers: this.getAppHeaders(),
      },
    ).pipe(
        take(1),
        mergeMap(res => from(res.regularWithdrawalDrafts.map(
          regularWithdrawalDraft => ({
            ...regularWithdrawalDraft, aml: res.aml,
          })
        ))),
        toArray(),
    );
  }

  // POST /api/v1/client/{clientAccountId}/regular-withdrawal-draft/{regularWithdrawalDraftId}/reject
  public rejectWithdrawalDraft(regularWithdrawalDraftId: number): Observable<void> {
    return this.http
      .post<void>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}v1/client/${this.loggedUser?.clientAccountId}/regular-withdrawal-draft/${regularWithdrawalDraftId}/reject`,
        {},
        {
          headers: this.getAppHeaders(),
        },
      ).pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // POST /api/v1/client/{clientAccountId}/portfolio/{portfolioId}/regular-withdrawal-draft/{regularWithdrawalDraftId}/accept
  public acceptWithdrawalDraft(investmentId: number, regularWithdrawalDraftId: number): Observable<void> {
    return this.http
      .post<void>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}v1/client/${this.loggedUser?.clientAccountId}/portfolio/${investmentId}/regular-withdrawal-draft/${regularWithdrawalDraftId}/accept`,
        {},
        {
          headers: this.getAppHeaders(),
        },
      ).pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // GET /api/v1/client/{clientAccountId}/regular-withdrawal/portfolio
  public getWithdrawalPortfolios(pageRequest: IPageRequest, type: EWithdrawalPortfolioType): Observable<IPortfolioOverviewPage> {
    return this.http
    .get<IPortfolioOverviewPage>(
      `${this.configService.getConfig(EnvironmentKeys.apiUrl)}v1/client/${this.loggedUser?.clientAccountId}/withdrawal-portfolio`,
        {
          headers: this.getAppHeaders(),
          params: this.toHttpParams({ ...pageRequest, withdrawalType: type })
        },
    )
    .pipe(
      take(1),
      catchError(this.handleError.bind(this)),
    );
  }

  // GET /api/v1/client/{clientAccountId}/portfolio/{portfolioId}/regular-withdrawal/limit
  public getRegularWithdrawalLimits(portfolioId: number, frequency: ERegularWithdrawalFrequency): Observable<IRegularWithdrawalLimits> {
    return this.http
      .get<IRegularWithdrawalLimits>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}v1/client/${this.loggedUser?.clientAccountId}/portfolio/${portfolioId}/regular-withdrawal/limit`,
        {
          headers: this.getAppHeaders(),
          params: {
            frequency
          }
        },
      )
      .pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // POST /api/v1/client/{clientAccountId}/regular-withdrawal
  public createRegularWithdrawal(input: INewRegularWithdrawalInput): Observable<void> {
    return this.http
    .post<void>(
      `${this.configService.getConfig(EnvironmentKeys.apiUrl)}v1/client/${this.loggedUser?.clientAccountId}/regular-withdrawal`,
      input,
      {
        headers: this.getAppHeaders(),
      },
    ).pipe(
      take(1),
      catchError(this.handleError.bind(this)),
    );
  }

  // GET /api/v1/client/{clientAccountId}/portfolio/{portfolioId}/withdrawable-amount
  public getWithdrawableAmount(portfolioId: number): Observable<IWithdrawableAmount> {
    return this.http
      .get<IWithdrawableAmount>(
        `${this.configService.getConfig(EnvironmentKeys.apiUrl)}v1/client/${this.loggedUser?.clientAccountId}/portfolio/${portfolioId}/withdrawable-amount`,
        {
          headers: this.getAppHeaders(),
        },
      )
      .pipe(
        take(1),
        catchError(this.handleError.bind(this)),
      );
  }

  // POST /api/v1/client/{id}/appearance-mode
  public setAppearanceMode(appearanceMode: EAppearanceMode): Observable<void> {
    return this.http
    .post<void>(
      `${this.configService.getConfig(EnvironmentKeys.apiUrl)}v1/client/${this.loggedUser?.clientAccountId}/appearance-mode`,
      { appearanceMode },
      {
        headers: this.getAppHeaders(),
      },
    ).pipe(
      take(1),
      catchError(this.handleError.bind(this)),
    );
  }
}
